/**
 * Font Manifest Data - Raw font definitions
 * 
 * This file contains only the data to avoid any Vite/Rollup resolution issues.
 */

export interface FontManifestEntry {
  font_uid: string;        // Backend file identifier
  displayName: string;     // User-facing name in UI
  family: string;          // CSS font-family for rendering
  weight: number;          // Font weight (100-900)
  style: 'normal' | 'italic' | 'oblique';
  category: 'serif' | 'sans-serif' | 'monospace' | 'display';
  court_compliant: boolean; // Approved for court filings
  description?: string;     // Optional description
}

export const FONT_MANIFEST_DATA: FontManifestEntry[] = [
  // ============================================================================
  // COURT-COMPLIANT SERIF FONTS
  // ============================================================================
  {
    font_uid: 'NimbusRoman-Regular.otf',
    displayName: 'Times New Roman',
    family: 'EdgeDocs Roman',
    weight: 400,
    style: 'normal',
    category: 'serif',
    court_compliant: true,
    description: 'Court-compliant Times New Roman equivalent (Nimbus Roman)'
  },

  // TeX Gyre Schola (Century Schoolbook) - Required for U.S. Supreme Court
  {
    font_uid: 'TeXGyreSchola-Regular.otf',
    displayName: 'Century Schoolbook',
    family: 'TeX Gyre Schola',
    weight: 400,
    style: 'normal',
    category: 'serif',
    court_compliant: true,
    description: 'Century Schoolbook equivalent - required for U.S. Supreme Court filings'
  },
  {
    font_uid: 'TeXGyreSchola-Italic.otf',
    displayName: 'Century Schoolbook Italic',
    family: 'TeX Gyre Schola',
    weight: 400,
    style: 'italic',
    category: 'serif',
    court_compliant: true,
    description: 'Century Schoolbook Italic - required for U.S. Supreme Court filings'
  },
  {
    font_uid: 'TeXGyreSchola-Bold.otf',
    displayName: 'Century Schoolbook Bold',
    family: 'TeX Gyre Schola',
    weight: 700,
    style: 'normal',
    category: 'serif',
    court_compliant: true,
    description: 'Century Schoolbook Bold - required for U.S. Supreme Court filings'
  },
  {
    font_uid: 'TeXGyreSchola-BoldItalic.otf',
    displayName: 'Century Schoolbook Bold Italic',
    family: 'TeX Gyre Schola',
    weight: 700,
    style: 'italic',
    category: 'serif',
    court_compliant: true,
    description: 'Century Schoolbook Bold Italic - required for U.S. Supreme Court filings'
  },

  // Liberation Serif (Times New Roman equivalent) - 4 variants
  {
    font_uid: 'LiberationSerif-Regular.ttf',
    displayName: 'Liberation Serif',
    family: 'Liberation Serif',
    weight: 400,
    style: 'normal',
    category: 'serif',
    court_compliant: true,
    description: 'Times New Roman equivalent - court compliant serif'
  },
  {
    font_uid: 'LiberationSerif-Italic.ttf',
    displayName: 'Liberation Serif Italic',
    family: 'Liberation Serif',
    weight: 400,
    style: 'italic',
    category: 'serif',
    court_compliant: true,
    description: 'Times New Roman Italic equivalent - court compliant serif'
  },
  {
    font_uid: 'LiberationSerif-Bold.ttf',
    displayName: 'Liberation Serif Bold',
    family: 'Liberation Serif',
    weight: 700,
    style: 'normal',
    category: 'serif',
    court_compliant: true,
    description: 'Times New Roman Bold equivalent - court compliant serif'
  },
  {
    font_uid: 'LiberationSerif-BoldItalic.ttf',
    displayName: 'Liberation Serif Bold Italic',
    family: 'Liberation Serif',
    weight: 700,
    style: 'italic',
    category: 'serif',
    court_compliant: true,
    description: 'Times New Roman Bold Italic equivalent - court compliant serif'
  },

  // ============================================================================
  // COURT-COMPLIANT SANS-SERIF FONTS
  // ============================================================================

  // Liberation Sans (Arial equivalent) - 4 variants
  {
    font_uid: 'LiberationSans-Regular.ttf',
    displayName: 'Liberation Sans',
    family: 'Liberation Sans',
    weight: 400,
    style: 'normal',
    category: 'sans-serif',
    court_compliant: true,
    description: 'Arial equivalent - court compliant sans-serif'
  },
  {
    font_uid: 'LiberationSans-Italic.ttf',
    displayName: 'Liberation Sans Italic',
    family: 'Liberation Sans',
    weight: 400,
    style: 'italic',
    category: 'sans-serif',
    court_compliant: true,
    description: 'Arial Italic equivalent - court compliant sans-serif'
  },
  {
    font_uid: 'LiberationSans-Bold.ttf',
    displayName: 'Liberation Sans Bold',
    family: 'Liberation Sans',
    weight: 700,
    style: 'normal',
    category: 'sans-serif',
    court_compliant: true,
    description: 'Arial Bold equivalent - court compliant sans-serif'
  },
  {
    font_uid: 'LiberationSans-BoldItalic.ttf',
    displayName: 'Liberation Sans Bold Italic',
    family: 'Liberation Sans',
    weight: 700,
    style: 'italic',
    category: 'sans-serif',
    court_compliant: true,
    description: 'Arial Bold Italic equivalent - court compliant sans-serif'
  },

  // ============================================================================
  // COURT-COMPLIANT MONOSPACE FONTS
  // ============================================================================

  // Liberation Mono (Courier New equivalent) - 4 variants
  {
    font_uid: 'LiberationMono-Regular.ttf',
    displayName: 'Liberation Mono',
    family: 'Liberation Mono',
    weight: 400,
    style: 'normal',
    category: 'monospace',
    court_compliant: true,
    description: 'Courier New equivalent - court compliant monospace'
  },
  {
    font_uid: 'LiberationMono-Italic.ttf',
    displayName: 'Liberation Mono Italic',
    family: 'Liberation Mono',
    weight: 400,
    style: 'italic',
    category: 'monospace',
    court_compliant: true,
    description: 'Courier New Italic equivalent - court compliant monospace'
  },
  {
    font_uid: 'LiberationMono-Bold.ttf',
    displayName: 'Liberation Mono Bold',
    family: 'Liberation Mono',
    weight: 700,
    style: 'normal',
    category: 'monospace',
    court_compliant: true,
    description: 'Courier New Bold equivalent - court compliant monospace'
  },
  {
    font_uid: 'LiberationMono-BoldItalic.ttf',
    displayName: 'Liberation Mono Bold Italic',
    family: 'Liberation Mono',
    weight: 700,
    style: 'italic',
    category: 'monospace',
    court_compliant: true,
    description: 'Courier New Bold Italic equivalent - court compliant monospace'
  },

  // ============================================================================
  // PROFESSIONAL SANS-SERIF FONTS
  // ============================================================================
  {
    font_uid: 'Inter-Regular.ttf',
    displayName: 'Inter',
    family: 'Inter',
    weight: 400,
    style: 'normal',
    category: 'sans-serif',
    court_compliant: false
  },
  {
    font_uid: 'Inter-Bold.ttf',
    displayName: 'Inter Bold',
    family: 'Inter',
    weight: 700,
    style: 'normal',
    category: 'sans-serif',
    court_compliant: false
  },
  {
    font_uid: 'Roboto-Regular.ttf',
    displayName: 'Roboto',
    family: 'Roboto',
    weight: 400,
    style: 'normal',
    category: 'sans-serif',
    court_compliant: false
  },
  
  // ============================================================================
  // CUSTOM FONTS (Backend-provisioned): Carlito, Noto Serif, Roboto Mono
  // These entries must match the FontResolver UIDs so both WASM and Canvas
  // render with identical fonts.
  // ============================================================================
  // Carlito (Calibri-compatible) - 4 variants
  {
    font_uid: 'carlito-400-normal-1760278134239.ttf',
    displayName: 'Carlito Regular',
    family: 'Carlito',
    weight: 400,
    style: 'normal',
    category: 'sans-serif',
    court_compliant: false
  },
  {
    font_uid: 'carlito-400-italic-1760278134232.ttf',
    displayName: 'Carlito Italic',
    family: 'Carlito',
    weight: 400,
    style: 'italic',
    category: 'sans-serif',
    court_compliant: false
  },
  {
    font_uid: 'carlito-700-normal-1760278134219.ttf',
    displayName: 'Carlito Bold',
    family: 'Carlito',
    weight: 700,
    style: 'normal',
    category: 'sans-serif',
    court_compliant: false
  },
  {
    font_uid: 'carlito-700-italic-1760278134225.ttf',
    displayName: 'Carlito Bold Italic',
    family: 'Carlito',
    weight: 700,
    style: 'italic',
    category: 'sans-serif',
    court_compliant: false
  },

  // Noto Serif - 9 variants
  {
    font_uid: 'noto-serif-100-normal-1760280814315.ttf',
    displayName: 'Noto Serif Thin',
    family: 'Noto Serif',
    weight: 100,
    style: 'normal',
    category: 'serif',
    court_compliant: false
  },
  {
    font_uid: 'noto-serif-200-normal-1760280814302.ttf',
    displayName: 'Noto Serif Extra Light',
    family: 'Noto Serif',
    weight: 200,
    style: 'normal',
    category: 'serif',
    court_compliant: false
  },
  {
    font_uid: 'noto-serif-300-normal-1760280814337.ttf',
    displayName: 'Noto Serif Light',
    family: 'Noto Serif',
    weight: 300,
    style: 'normal',
    category: 'serif',
    court_compliant: false
  },
  {
    font_uid: 'noto-serif-400-normal-1760280814357.ttf',
    displayName: 'Noto Serif Regular',
    family: 'Noto Serif',
    weight: 400,
    style: 'normal',
    category: 'serif',
    court_compliant: false
  },
  {
    font_uid: 'noto-serif-400-italic-1760280814325.ttf',
    displayName: 'Noto Serif Italic',
    family: 'Noto Serif',
    weight: 400,
    style: 'italic',
    category: 'serif',
    court_compliant: false
  },
  {
    font_uid: 'noto-serif-500-normal-1760280814344.ttf',
    displayName: 'Noto Serif Medium',
    family: 'Noto Serif',
    weight: 500,
    style: 'normal',
    category: 'serif',
    court_compliant: false
  },
  {
    font_uid: 'noto-serif-600-normal-1760280814308.ttf',
    displayName: 'Noto Serif SemiBold',
    family: 'Noto Serif',
    weight: 600,
    style: 'normal',
    category: 'serif',
    court_compliant: false
  },
  {
    font_uid: 'noto-serif-700-normal-1760280814283.ttf',
    displayName: 'Noto Serif Bold',
    family: 'Noto Serif',
    weight: 700,
    style: 'normal',
    category: 'serif',
    court_compliant: false
  },
  {
    font_uid: 'noto-serif-700-italic-1760280814293.ttf',
    displayName: 'Noto Serif Bold Italic',
    family: 'Noto Serif',
    weight: 700,
    style: 'italic',
    category: 'serif',
    court_compliant: false
  },

  // Roboto Mono - 6 variants
  {
    font_uid: 'roboto-mono-100-italic-1760280977722.ttf',
    displayName: 'Roboto Mono Thin Italic',
    family: 'Roboto Mono',
    weight: 100,
    style: 'italic',
    category: 'monospace',
    court_compliant: false
  },
  {
    font_uid: 'roboto-mono-400-normal-1760280977718.ttf',
    displayName: 'Roboto Mono Regular',
    family: 'Roboto Mono',
    weight: 400,
    style: 'normal',
    category: 'monospace',
    court_compliant: false
  },
  {
    font_uid: 'roboto-mono-400-italic-1760280977706.ttf',
    displayName: 'Roboto Mono Italic',
    family: 'Roboto Mono',
    weight: 400,
    style: 'italic',
    category: 'monospace',
    court_compliant: false
  },
  {
    font_uid: 'roboto-mono-500-normal-1760280977712.ttf',
    displayName: 'Roboto Mono Medium',
    family: 'Roboto Mono',
    weight: 500,
    style: 'normal',
    category: 'monospace',
    court_compliant: false
  },
  {
    font_uid: 'roboto-mono-700-normal-1760280977695.ttf',
    displayName: 'Roboto Mono Bold',
    family: 'Roboto Mono',
    weight: 700,
    style: 'normal',
    category: 'monospace',
    court_compliant: false
  },
  {
    font_uid: 'roboto-mono-700-italic-1760280977701.ttf',
    displayName: 'Roboto Mono Bold Italic',
    family: 'Roboto Mono',
    weight: 700,
    style: 'italic',
    category: 'monospace',
    court_compliant: false
  },
];

